// Internal comms (Phase C) — company announcements feed. Everyone reads,
// acknowledges and reacts; HR publishes and manages.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, Heart, Pin, Check, Trash2, Megaphone, Cake, CalendarDays, Gift, Award } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const CATS = ['General', 'Policy', 'Event', 'Urgent', 'Celebration'];
const catTone = (c) => (c === 'Urgent' ? 'error' : c === 'Celebration' ? 'success' : c === 'Policy' ? 'warning' : 'info');
const blank = { title: '', body: '', category: 'General', pinned: false, requireAck: false, companyWide: false };
const EVENT_KINDS = ['Event', 'Holiday', 'TownHall', 'Social', 'Training', 'Other'];
const whenLabel = (e) => { try { const d = new Date(e.startsAt); return d.toLocaleString(undefined, e.allDay ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' }); } catch { return e.startsAt; } };
const inDaysLabel = (n) => (n === 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`);
// UTC ISO → datetime-local value in the viewer's wall-clock (no offset shift on edit).
const toLocal = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d)) return ''; return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export default function Announcements({ showToast }) {
  const { isHR } = useAuth();
  const [feed, setFeed] = useState(null);
  const [counts, setCounts] = useState({});
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [celebrations, setCelebrations] = useState(null);
  const [events, setEvents] = useState(null);
  const [eventForm, setEventForm] = useState(null);

  const load = () => {
    api.announcementFeed().then(setFeed).catch(() => setFeed([]));
    api.celebrations(30).then(setCelebrations).catch(() => setCelebrations({ birthdays: [], anniversaries: [] }));
    api.events().then(setEvents).catch(() => setEvents([]));
    if (isHR) api.announcementsAll().then((rows) => setCounts(Object.fromEntries(rows.map((r) => [r.id, r])))).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.startsAt || busy) return; setBusy(true);
    try {
      // Resolve the datetime-local wall-clock to an absolute UTC instant in the
      // browser's zone, so it's unambiguous regardless of the server's timezone
      // (toLocal() inverts this exactly on edit).
      const toIso = (s) => (s ? new Date(s).toISOString() : null);
      const payload = { ...eventForm, startsAt: toIso(eventForm.startsAt), endsAt: toIso(eventForm.endsAt) };
      if (eventForm.id) await api.updateEvent(eventForm.id, payload); else await api.createEvent(payload);
      showToast?.('Event saved'); setEventForm(null); load();
    } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const removeEvent = async (ev) => { try { await api.deleteEvent(ev.id); load(); } catch (e) { showToast?.(e.message); } };

  const like = async (a) => { try { await api.likeAnnouncement(a.id); load(); } catch (e) { showToast?.(e.message); } };
  const ack = async (a) => { try { await api.ackAnnouncement(a.id); showToast?.('Acknowledged'); load(); } catch (e) { showToast?.(e.message); } };
  const remove = async (a) => { try { await api.deleteAnnouncement(a.id); load(); } catch (e) { showToast?.(e.message); } };
  const togglePin = async (a) => { try { await api.updateAnnouncement(a.id, { pinned: !a.pinned }); load(); } catch (e) { showToast?.(e.message); } };
  const publish = async () => {
    if (!form.title.trim() || !form.body.trim() || busy) return; setBusy(true);
    try { await api.createAnnouncement(form); showToast?.('Announcement published'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };

  if (!feed) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Announcements" sub="Company news, events & celebrations" />
        {isHR && <PrimaryBtn onClick={() => setForm({ ...blank })}><Plus size={14} style={{ marginRight: 6 }} /> New announcement</PrimaryBtn>}
      </div>

      {/* Celebrations (1.6) — upcoming birthdays & work anniversaries */}
      {celebrations && (celebrations.birthdays?.length > 0 || celebrations.anniversaries?.length > 0) && (
        <Card style={{ padding: 16 }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 10 }}><Gift size={16} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Celebrations</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {celebrations.birthdays.map((b) => (
              <div key={`b-${b.id}`} className="flex items-center gap-2" style={{ padding: '8px 10px', background: AG.n50, borderRadius: 8 }}>
                <Cake size={15} style={{ color: AG.primary, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: AG.text }}><b>{b.name}</b>{b.turning ? ` turns ${b.turning}` : "'s birthday"} <span style={{ color: AG.n500 }}>· {inDaysLabel(b.inDays)}</span></span>
              </div>
            ))}
            {celebrations.anniversaries.map((a) => (
              <div key={`a-${a.id}`} className="flex items-center gap-2" style={{ padding: '8px 10px', background: AG.n50, borderRadius: 8 }}>
                <Award size={15} style={{ color: AG.successDark, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: AG.text }}><b>{a.name}</b> · {a.years} yr{a.years > 1 ? 's' : ''} <span style={{ color: AG.n500 }}>· {inDaysLabel(a.inDays)}</span></span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Events calendar (1.6) */}
      <Card style={{ padding: 16 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <div className="flex items-center gap-2"><CalendarDays size={16} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Upcoming events</span></div>
          {isHR && <OutlineBtn onClick={() => setEventForm({ title: '', description: '', kind: 'Event', startsAt: '', endsAt: '', allDay: false, location: '' })} style={{ padding: '5px 12px', fontSize: 12.5 }}><Plus size={13} style={{ marginRight: 4 }} /> Add</OutlineBtn>}
        </div>
        {!events || events.length === 0 ? <p style={{ fontSize: 13, color: AG.n500, margin: 0 }}>No upcoming events.</p> : (
          <div style={{ display: 'grid', gap: 6 }}>
            {events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-2" style={{ padding: '8px 10px', border: `1px solid ${AG.divider}`, borderRadius: 8 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: AG.text }}>{ev.title} <Pill tone="neutral">{ev.kind}</Pill></div>
                  <div style={{ fontSize: 12, color: AG.n500, marginTop: 1 }}>{whenLabel(ev)}{ev.location ? ` · ${ev.location}` : ''}</div>
                </div>
                {isHR && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEventForm({ id: ev.id, title: ev.title, description: ev.description || '', kind: ev.kind, startsAt: ev.startsAt ? toLocal(ev.startsAt) : '', endsAt: ev.endsAt ? toLocal(ev.endsAt) : '', allDay: ev.allDay, location: ev.location || '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, fontSize: 12, fontWeight: 600 }}>Edit</button>
                    <button onClick={() => removeEvent(ev)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {feed.length === 0 ? (
        <Card><EmptyState icon={Megaphone} title="No announcements" desc={isHR ? 'Publish the first company update.' : 'Company updates will appear here.'} /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {feed.map((a) => (
            <Card key={a.id} style={{ padding: 18, borderLeft: a.pinned ? `3px solid ${AG.primary}` : undefined }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  {a.pinned && <Pin size={14} style={{ color: AG.primary }} />}
                  <span style={{ fontWeight: 700, color: AG.text }}>{a.title}</span>
                  <Pill tone={catTone(a.category)}>{a.category}</Pill>
                  {a.requireAck && <Pill tone={a.acked ? 'success' : 'warning'}>{a.acked ? 'Acknowledged' : 'Ack required'}</Pill>}
                </div>
                {isHR && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => togglePin(a)} title="Pin" style={{ background: 'none', border: 'none', cursor: 'pointer', color: a.pinned ? AG.primary : AG.n400, padding: 4 }}><Pin size={15} /></button>
                    <button onClick={() => remove(a)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, padding: 4 }}><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 14, color: AG.n600, whiteSpace: 'pre-wrap', margin: '8px 0' }}>{a.body}</div>
              <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 11, color: AG.n400 }}>{a.createdAt?.slice(0, 10)}{isHR && counts[a.id] ? ` · ${counts[a.id].ackCount} acks` : ''}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => like(a)} className="flex items-center gap-1" style={{ background: 'none', border: 'none', cursor: 'pointer', color: a.liked ? AG.error : AG.n500, fontSize: 13, fontWeight: 500 }}>
                    <Heart size={15} fill={a.liked ? AG.error : 'none'} /> {a.likeCount}
                  </button>
                  {a.requireAck && !a.acked && <OutlineBtn onClick={() => ack(a)}><Check size={14} style={{ marginRight: 4 }} /> Acknowledge</OutlineBtn>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>New announcement</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Message" multiline minRows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} sx={{ mb: 1.5 }} />
              <label style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 8 }}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
              <div className="flex items-center" style={{ flexWrap: 'wrap' }}>
                <FormControlLabel control={<Checkbox checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />} label="Pin to top" />
                <FormControlLabel control={<Checkbox checked={form.requireAck} onChange={(e) => setForm({ ...form, requireAck: e.target.checked })} />} label="Require acknowledgment" />
                <FormControlLabel control={<Checkbox checked={form.companyWide} onChange={(e) => setForm({ ...form, companyWide: e.target.checked })} />} label="Company-wide" />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={publish} disabled={busy}>Publish</PrimaryBtn></DialogActions>
      </Dialog>

      {/* Event dialog (1.6) */}
      <Dialog open={!!eventForm} onClose={() => !busy && setEventForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{eventForm?.id ? 'Edit event' : 'New event'}</DialogTitle>
        <DialogContent>
          {eventForm && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <TextField size="small" label="Title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} />
              <div className="flex gap-2">
                <label style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Kind</span><select className="ag-input" value={eventForm.kind} onChange={(e) => setEventForm({ ...eventForm, kind: e.target.value })}>{EVENT_KINDS.map((k) => <option key={k}>{k}</option>)}</select></label>
                <TextField size="small" label="Location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} sx={{ flex: 1 }} />
              </div>
              <div className="flex gap-2">
                <TextField size="small" type="datetime-local" label="Starts" InputLabelProps={{ shrink: true }} value={eventForm.startsAt} onChange={(e) => setEventForm({ ...eventForm, startsAt: e.target.value })} sx={{ flex: 1 }} />
                <TextField size="small" type="datetime-local" label="Ends" InputLabelProps={{ shrink: true }} value={eventForm.endsAt} onChange={(e) => setEventForm({ ...eventForm, endsAt: e.target.value })} sx={{ flex: 1 }} />
              </div>
              <TextField size="small" label="Description" multiline minRows={2} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} />
              <FormControlLabel control={<Checkbox checked={eventForm.allDay} onChange={(e) => setEventForm({ ...eventForm, allDay: e.target.checked })} />} label="All day" />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setEventForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={saveEvent} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
